import React, { useEffect, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { ScanLine, Check, Ticket } from "lucide-react";
import { api, formatError, fmtDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Screen, PageHeader, Empty } from "../components/ui-kit";

export default function CheckIn() {
  const { user, checkAuth } = useAuth();
  const [targets, setTargets] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/checkin/targets").then((r) => setTargets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const checkIn = async (t) => {
    setBusy(t.ref_id);
    try {
      const res = await api.post("/checkin", { ref_type: t.ref_type, ref_id: t.ref_id });
      toast.success("Checked in", { description: `${t.title}${res.data.entry.credits_used ? ` · -${res.data.entry.credits_used} pass` : ""}` });
      await checkAuth();
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <Screen>
      <PageHeader overline="Attendance" title="Check-in" />
      <div className="rounded-3xl bg-white/5 border border-white/10 p-7 flex flex-col items-center">
        <div className="flex items-center gap-2 text-[#A19DB0] text-xs mb-5"><ScanLine size={14} /> Show this at the studio desk</div>
        <div className="bg-white p-4 rounded-2xl qr-pulse" data-testid="member-qr">
          <QRCode value={JSON.stringify({ uid: user.id, name: user.name })} size={172} fgColor="#0D0B14" bgColor="#FFFFFF" />
        </div>
        <p className="font-display text-lg font-semibold mt-5">{user.name}</p>
        <p className="text-[#A19DB0] text-xs">Member · {user.credits} class passes</p>
      </div>

      <h2 className="font-display text-xl font-semibold mt-8 mb-3">Ready to check in</h2>
      <p className="text-[#A19DB0] text-xs -mt-2 mb-4">Scan a class/event QR at the studio, or tap to check in here.</p>
      {targets.length === 0 ? (
        <Empty icon={Ticket} text="Book a class or join an event to check in." />
      ) : (
        <div className="space-y-3">
          {targets.map((t) => (
            <div key={`${t.ref_type}-${t.ref_id}`} data-testid={`checkin-target-${t.ref_id}`} className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-4">
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{t.title}</p>
                <p className="text-[#A19DB0] text-xs">{t.starts_at ? fmtDateTime(t.starts_at) : t.ref_type === "event" ? "Event" : ""} · {t.code}</p>
              </div>
              {t.checked_in ? (
                <span className="flex items-center gap-1 text-[#9D4EDD] text-sm font-medium"><Check size={16} /> Done</span>
              ) : (
                <button onClick={() => checkIn(t)} disabled={busy === t.ref_id} data-testid={`checkin-btn-${t.ref_id}`}
                  className="bg-[#D4AF37] text-black font-semibold rounded-full px-4 py-2 text-sm active:scale-95 transition-transform disabled:opacity-60">
                  {busy === t.ref_id ? "…" : "Check in"}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
